import { Button, FormControl, Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useCallback, useState } from 'react'
import TelephoneInput from 'react-phone-number-input/input'

import {
  formatPhoneNumberForDisplay,
  formatPhoneNumberForSave,
} from '../../lib/phone'
import { restoreState } from '../../lib/localstorage'
import { ensureAbsoluteUrl } from '../../utils/url'
import { sortObjectsAlphabetically } from '../../lib/sort'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import AvatarImage from '../account/avatar-image'
import BusinessBadge from './business-badge'
import CivWallet from '../../components/CivWallet/civ-wallet'
import CausePicker from '../../components/CausePicker/CausePicker'
import Loading from '../../components/loading'
import SponsorshipAdMessage from './sponsorship-ad-message'
import SponsorshipProgram from './sponsorship-program'
import states from '../../constants/states'
import useApi from '../../hooks/useApi'
import BusinessAdminModal from '../../components/Business/BusinessAdminModal'
import MediaUpload from '../../components/Business/MediaUpload'

const MAX_BUSINESS_ADMINS = 5
const COVER_PHOTO_DIMENSIONS = '1920x600'

export default function BusinessEdit() {
  console.count('BusinessEdit render')
  const { t } = useTranslation()

  const { currentUser, locale, hasBetaFeature } = useCurrentUser()
  const localeKey = locale === 'es' ? 'es_name' : 'en_name'
  const { business_id } = currentUser || {}

  const { getBusiness, getIndustries, updateBusiness, addBusinessAdmin, removeBusinessAdmin } = useApi()

  const [hasBusinessChanged, setHasBusinessChanged] = useState(false)
  const [businessLoading, setBusinessLoading] = useState(false)
  const [business, setBusiness] = useState()
  const [industries, setIndustries] = useState(() =>
    restoreState('industries', []),
  )
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false)
  const [businessAdmins, setBusinessAdmins] = useState([])
  const [businessAdminsInitial, setBusinessAdminsInitial] = useState([])
  const [hasSocialNetworksChanged, setHasSocialNetworksChanged] = useState(false)
  const [hasMediaChanged, setHasMediaChanged] = useState(false)
  const [hasMessageChanged, setHasMessageChanged] = useState(false)
  const [hasAdminsChanged, setHasAdminsChanged] = useState(false)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    if (!business_id) return
    // Prevent re-fetching if business is already loaded
    if (business?.id) return
    
    setBusinessLoading(true)
    try {
      const { data } = await getBusiness(business_id)
      if (data) {
        setBusiness({ ...data, industry_id: data.industry?.id })
        // only update businessAdmins if the API returns them, otherwise preserve current state
        if (data.business_admins !== undefined) {
          const admins = data.business_admins || []
          setBusinessAdmins(admins)
          setBusinessAdminsInitial(admins)
        }
      }

      const { data: _industries } = await getIndustries()
      if (_industries) {
        setIndustries(sortObjectsAlphabetically(_industries, localeKey))
      }
    } catch (error) {
      console.error('Error fetching business data:', error)
      setError(t('business.edit.save_failed_retry'))
    } finally {
      setBusinessLoading(false)
    }
  }, [getBusiness, getIndustries, business_id, localeKey, t, business?.id])

  const onSaveBusiness = useCallback(async () => {
    if (!business) return
    
    setBusinessLoading(true)
    setError(null)
    
    const params = {
      business: {
        address_line_1: business.address_line_1,
        address_line_2: business.address_line_2,
        category_ids: business.causeIds,
        city: business.city,
        ein: business.ein,
        industry_id: business.industry_id,
        name: business.name,
        phone: business.phone ? formatPhoneNumberForSave(business.phone) : '',
        state: business.state,
        website_url: ensureAbsoluteUrl(business.website_url) || '',
        zip: business.zip,
        facebook_link: ensureAbsoluteUrl(business.facebook_link) || '',
        linkedin_link: ensureAbsoluteUrl(business.linkedin_link) || '',
        instagram_link: ensureAbsoluteUrl(business.instagram_link) || '',
        youtube_link: ensureAbsoluteUrl(business.youtube_link) || '',
        tiktok_link: ensureAbsoluteUrl(business.tiktok_link) || '',
        community_statement: business.community_statement,
      },
      cover_photo_id: business.cover_photo?.id || business.cover_photo_id,
      banner_ad_graphics_id: business.banner_ad_graphics?.id || business.banner_ad_graphics_id,
    }
    
    try {
      const response = await updateBusiness(business_id, params)
      if (response.errors) {
        setError(response.errors?.full_messages?.[0] || t('business.edit.save_failed'))
      } else {
        setError(null)
        if (response.data) {
          setBusiness(prev => ({
            ...prev,
            ...response.data,
            industry_id: response.data.industry?.id,
            cover_photo: response.data.cover_photo !== undefined ? response.data.cover_photo : prev.cover_photo,
            cover_photo_id: response.data.cover_photo_id !== undefined ? response.data.cover_photo_id : prev.cover_photo_id,
            banner_ad_graphics: response.data.banner_ad_graphics !== undefined ? response.data.banner_ad_graphics : prev.banner_ad_graphics,
            banner_ad_graphics_id: response.data.banner_ad_graphics_id !== undefined ? response.data.banner_ad_graphics_id : prev.banner_ad_graphics_id,
          }))
          if (response.data.business_admins !== undefined) {
            const admins = response.data.business_admins || []
            setBusinessAdmins(admins)
            setBusinessAdminsInitial(admins)
          }
        }
        setHasBusinessChanged(false)
        setHasSocialNetworksChanged(false)
        setHasMediaChanged(false)
        setHasMessageChanged(false)
      }
    } catch (err) {
      setError(t('business.edit.save_failed_retry'))
    } finally {
      setBusinessLoading(false)
    }
  }, [business, business_id, updateBusiness, t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const updateBusinessField = useCallback((field, value) => {
    setBusiness(prev => {
      if (!prev) {
        if (business_id) {
          return { id: business_id, [field]: value }
        }
        return prev
      }
      return { ...prev, [field]: value }
    })
    setHasBusinessChanged(true)
  }, [business_id])

  const updateBusinessFieldWithFlag = useCallback((field, value, flagSetter) => {
    setBusiness(prev => {
      if (!prev) {
        if (business_id) {
          return { id: business_id, [field]: value }
        }
        return prev
      }
      return { ...prev, [field]: value }
    })
    flagSetter(true)
    setHasBusinessChanged(true)
  }, [business_id])

  const imageBusinessOnChange = async mediaAttachment => {
    const response = await updateBusiness(business_id, {
      media_attachment_id: mediaAttachment.id,
      business: null,
    })
    if (response.errors) {
      setError(response.errors?.full_messages?.[0] || t('business.edit.profile_picture_failed'))
    } else {
      setBusiness(prev => ({
        ...prev,
        media_attachment: { ...mediaAttachment },
      }))
    }
  }

  const handleCoverPhotoChange = useCallback(
    mediaAttachment => {
      setBusiness(prev => ({
        ...prev,
        cover_photo: mediaAttachment,
        cover_photo_id: mediaAttachment?.id,
      }))
      setHasMediaChanged(true)
      setHasBusinessChanged(true)
    },
    [],
  )

  const handleBannerAdGraphicsChange = useCallback(
    mediaAttachment => {
      setBusiness(prev => ({
        ...prev,
        banner_ad_graphics: mediaAttachment,
        banner_ad_graphics_id: mediaAttachment?.id,
      }))
      setHasMediaChanged(true)
      setHasBusinessChanged(true)
    },
    [],
  )

  const handleAddAdmin = useCallback(
    admin => {
      setBusinessAdmins(prev => {
        if (prev.length < MAX_BUSINESS_ADMINS && !prev.find(a => a.id === admin.id)) {
          setHasAdminsChanged(true)
          return [...prev, admin]
        }
        return prev
      })
    },
    [],
  )

  const handleRemoveAdmin = useCallback(
    admin => {
      setBusinessAdmins(prev => {
        setHasAdminsChanged(true)
        return prev.filter(a => a.id !== admin.id)
      })
    },
    [],
  )

  const onSaveAdmins = useCallback(async () => {
    if (!business_id) return
    
    setBusinessLoading(true)
    setError(null)
    
    try {
      const adminsToAdd = businessAdmins.filter(
        a => !businessAdminsInitial.find(initial => initial.id === a.id)
      )
      const adminsToRemove = businessAdminsInitial.filter(
        initial => !businessAdmins.find(current => current.id === initial.id)
      )
      
      if (adminsToAdd.length > 0 || adminsToRemove.length > 0) {
        if (adminsToAdd.length) {
          await Promise.all(
            adminsToAdd.map(admin =>
              addBusinessAdmin({ businessId: business_id, userId: admin.id }),
            ),
          )
        }
        
        if (adminsToRemove.length) {
          await Promise.all(
            adminsToRemove.map(admin =>
              removeBusinessAdmin({ businessId: business_id, userId: admin.id }),
            ),
          )
        }
        
        const { data: refreshedBusiness } = await getBusiness(business_id)
        if (refreshedBusiness?.business_admins !== undefined) {
          setBusinessAdmins(refreshedBusiness.business_admins || [])
          setBusinessAdminsInitial(refreshedBusiness.business_admins || [])
        }
        
        setHasAdminsChanged(false)
      }
    } catch (err) {
      setError(t('business.edit.save_failed_retry'))
    } finally {
      setBusinessLoading(false)
    }
  }, [business_id, businessAdmins, businessAdminsInitial, addBusinessAdmin, removeBusinessAdmin, getBusiness, t])

  if (!business) {
    return <Loading show />
  }

  return (
    <>
      {businessLoading && <Loading show />}
      {error && (
        <div className="container mt-3">
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}
      <br />
      <div className="container">
        <div className="row">
          <h1 className="fs-1">{t('business.edit.title')}</h1>
        </div>
      </div>
      <hr />
      <div className="container">
        <div className="row">
          <h1>{t('business.edit.business_profile')}</h1>
        </div>
        <div className="row">
          <div className="col">
            <AvatarImage
              imageUrl={
                business?.media_attachment?.thumbnail_url ??
                business?.media_attachment?.direct_upload_url ??
                '/images/default-business.png'
              }
              mediaAttachmentType="business"
              onChange={imageBusinessOnChange}
            />
          </div>
          {/* <BusinessBadge
            iconName={'award'}
            iconStyle={'business-badge-icon'}
            label={t('business.edit.badge')}
          /> */}
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="name">{t('business.edit.name')}</label>
            <input
              className="form-control"
              id="name"
              name="name"
              type="text"
              value={business?.name || ''}
              onChange={e => updateBusinessField('name', e.target.value)}
            />
          </div>
          <div className="form-group col-md-3">
            <label htmlFor="ein">{t('business.edit.ein')}</label>
            <input
              className="form-control"
              id="ein"
              name="ein"
              type="text"
              value={business?.ein || ''}
              onChange={e => updateBusinessField('ein', e.target.value)}
            />
          </div>
          <div className="form-group col-md-3">
            <label htmlFor="phone">{t('business.edit.contact_number')}</label>
            <TelephoneInput
              className="form-control"
              country="US"
              international={false}
              onChange={phone => updateBusinessField('phone', phone)}
              placeholder="(123) 456-7890"
              value={business?.phone || ''}
            />
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="website_url">{t('business.edit.website')}</label>
            <input
              className="form-control"
              id="website_url"
              name="website_url"
              type="text"
              value={business?.website_url || ''}
              onChange={e => {
                updateBusinessField('website_url', e.target.value.replace(/(^\w+:|^)\/\//, ''))
              }}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="industry_id">{t('business.edit.industry')}</label>
            <select
              name="industry_id"
              id="industry_id"
              className="form-control"
              value={business?.industry_id ?? ''}
              onChange={e => {
                updateBusinessField('industry_id', e.target.value)
              }}
            >
              <option value="">
                {t('users.edit.no_gender')}
              </option>
              {industries.map(s => (
                <option value={s.id} key={s.id}>
                  {s[localeKey]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-6">
            <label htmlFor="address_line_1">{t('business.edit.address')}</label>
            <input
              className="form-control"
              id="address_line_1"
              name="address_line_1"
              type="text"
              value={business?.address_line_1 || ''}
              onChange={e => updateBusinessField('address_line_1', e.target.value)}
            />
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="address_line_2">
              {t('business.edit.suite_number')}
            </label>
            <input
              className="form-control"
              id="address_line_2"
              name="address_line_2"
              type="text"
              value={business?.address_line_2 || ''}
              onChange={e => updateBusinessField('address_line_2', e.target.value)}
            />
          </div>
        </div>
        <div className="row form-row">
          <div className="form-group col-md-4">
            <label htmlFor="city">{t('business.edit.city')}</label>
            <input
              className="form-control"
              id="city"
              name="city"
              type="text"
              value={business?.city || ''}
              onChange={e => updateBusinessField('city', e.target.value)}
            />
          </div>
          <div className="form-group col-md-4">
            <label htmlFor="state">{t('business.edit.state')}</label>
            <select
              name="state"
              id="state"
              className="form-control"
              value={business?.state ?? ''}
              onChange={e => {
                updateBusinessField('state', e.target.value)
              }}
            >
              <option value="">
                {t('users.edit.no_gender')}
              </option>
              {states.map(s => (
                <option value={s.value} key={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-4">
            <label htmlFor="zip">{t('business.edit.zip')}</label>
            <input
              className="form-control"
              id="zip"
              name="zip"
              type="text"
              value={business?.zip || ''}
              onChange={e => updateBusinessField('zip', e.target.value)}
            />
          </div>
        </div>

        <hr />

        <div className="container">
          <div className="row">
            <h2>Social Networks</h2>
          </div>
          <div className="row form-row">
            <div className="form-group col-md-6">
              <label htmlFor="facebook_link">Facebook</label>
              <div className="input-group">
                <input
                  className="form-control"
                  id="facebook_link"
                  name="facebook_link"
                  type="text"
                  placeholder="https://facebook.com/username"
                  value={business?.facebook_link || ''}
                  onChange={e => updateBusinessFieldWithFlag('facebook_link', e.target.value, setHasSocialNetworksChanged)}
                />
                {business?.facebook_link && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => updateBusinessFieldWithFlag('facebook_link', '', setHasSocialNetworksChanged)}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="linkedin_link">LinkedIn</label>
              <input
                className="form-control"
                id="linkedin_link"
                name="linkedin_link"
                type="text"
                placeholder="https://linkedin.com/company/username"
                value={business?.linkedin_link || ''}
                onChange={e => updateBusinessFieldWithFlag('linkedin_link', e.target.value, setHasSocialNetworksChanged)}
              />
            </div>
          </div>
          <div className="row form-row">
            <div className="form-group col-md-6">
              <label htmlFor="instagram_link">Instagram</label>
              <input
                className="form-control"
                id="instagram_link"
                name="instagram_link"
                type="text"
                placeholder="https://instagram.com/username"
                value={business?.instagram_link || ''}
                onChange={e => updateBusinessFieldWithFlag('instagram_link', e.target.value, setHasSocialNetworksChanged)}
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="youtube_link">YouTube</label>
              <input
                className="form-control"
                id="youtube_link"
                name="youtube_link"
                type="text"
                placeholder="https://youtube.com/@username"
                value={business?.youtube_link || ''}
                onChange={e => updateBusinessFieldWithFlag('youtube_link', e.target.value, setHasSocialNetworksChanged)}
              />
            </div>
          </div>
          <div className="row form-row">
            <div className="form-group col-md-6">
              <label htmlFor="tiktok_link">TikTok</label>
              <input
                className="form-control"
                id="tiktok_link"
                name="tiktok_link"
                type="text"
                placeholder="https://tiktok.com/@username"
                value={business?.tiktok_link || ''}
                onChange={e => updateBusinessFieldWithFlag('tiktok_link', e.target.value, setHasSocialNetworksChanged)}
              />
            </div>
          </div>
          <input
            className="btn btn-primary"
            onClick={onSaveBusiness}
            type="submit"
            value={t('business.edit.save')}
            disabled={!hasSocialNetworksChanged}
          />
        </div>

        <hr />

        {!businessLoading ? (
          <CausePicker
            onChange={causeIds => updateBusinessField('causeIds', causeIds)}
            selectedCauseIds={business?.categories?.map(c => c.id) ?? []}
            title={<></>}
          />
        ) : null}

        <hr />

        <div className="container">
          <div className="row">
            <h2>Business Page Cover Photo</h2>
          </div>
          <div className="row">
            <div className="col">
              <MediaUpload
                imageUrl={
                  business?.cover_photo?.thumbnail_url ??
                  business?.cover_photo?.direct_upload_url ??
                  (business?.cover_photo_id
                    ? `/api/media_attachments/${business.cover_photo_id}`
                    : null)
                }
                onChange={handleCoverPhotoChange}
                label="Business Page Cover Photo"
                dimensions={COVER_PHOTO_DIMENSIONS}
              />
            </div>
          </div>
        </div>

        <hr />

        <div className="container">
          <div className="row">
            <h2>Business Community Message</h2>
            <h6 className="block-description">
              Share a message about your business and community involvement
            </h6>
          </div>
          <div className="row form-row">
            <div className="form-group col-md-12">
              <FormControl
                as="textarea"
                rows={5}
                value={business?.community_statement || ''}
                onChange={e => updateBusinessFieldWithFlag('community_statement', e.target.value, setHasMessageChanged)}
                placeholder="Enter your business community message..."
              />
            </div>
          </div>
          <input
            className="btn btn-primary"
            onClick={onSaveBusiness}
            type="submit"
            value={t('business.edit.save')}
            disabled={!hasMessageChanged}
          />
        </div>

        <hr />

        <div className="container">
          <div className="row">
            <h2>Sponsor Banner Advertising</h2>
          </div>
          <div className="row">
            <div className="col">
              <MediaUpload
                imageUrl={
                  business?.banner_ad_graphics?.thumbnail_url ??
                  business?.banner_ad_graphics?.direct_upload_url ??
                  (business?.banner_ad_graphics_id
                    ? `/api/media_attachments/${business.banner_ad_graphics_id}`
                    : null)
                }
                onChange={handleBannerAdGraphicsChange}
                label="Sponsor Banner Advertising"
              />
            </div>
          </div>
        </div>

        <hr />

        <div className="container">
          <div className="row">
            <div className="col d-flex justify-content-between align-items-center">
              <h2>{t('event.add_admins_title')}</h2>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setIsAdminModalOpen(true)}
              >
                + {t('event.add_admins')}
              </button>
            </div>
          </div>
          {businessAdmins.length > 0 && (
            <div className="row mt-3">
              <div className="col">
                {businessAdmins.map(admin => (
                  <div key={admin.id} className="d-inline-block me-2 mb-2">
                    <span className="badge bg-secondary p-2">
                      {admin.name} (@{admin.nickname})
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        aria-label="Remove"
                        onClick={() => handleRemoveAdmin(admin)}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <input
            className="btn btn-primary mt-3"
            onClick={onSaveAdmins}
            type="submit"
            value={t('business.edit.save')}
            disabled={!hasAdminsChanged}
          />
        </div>

        <input
          className="btn btn-primary"
          id="settings-form-submit"
          onClick={onSaveBusiness}
          type="submit"
          value={t('business.edit.save')}
          disabled={!hasBusinessChanged}
        />
      </div>
      {hasBetaFeature('pf_business') && business ? (
        <>
          <hr />
          <CivWallet
            data={business}
            setData={setBusiness}
            locale={locale}
            updateData={updateBusiness}
            origin={'business'}
          />
          <hr />
          <SponsorshipProgram businessData={business} />
          <br />
          <SponsorshipAdMessage businessData={business} />
        </>
      ) : null}
      <BusinessAdminModal
        admins={businessAdmins}
        isOpen={isAdminModalOpen}
        onAddAdmin={handleAddAdmin}
        onClose={() => setIsAdminModalOpen(false)}
        onRemoveAdmin={handleRemoveAdmin}
      />
      <br />
    </>
  )
}
